export interface DiversifiablePhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

type CategoryMatchers = ReadonlyArray<readonly [category: string, pattern: RegExp]>;

/** Order matters: the first match wins, so more specific spaces come first. */
export const APARTMENT_PHOTO_CATEGORIES: CategoryMatchers = [
  ['bathroom', /\b(bath|baths|bathroom|bathrooms|shower|tub|vanity|vanities|powder room|ensuite|en suite)\b/],
  ['closet', /\b(closet|closets|walk in|wardrobe)\b/],
  ['laundry', /\b(laundry|washer|dryer|w d)\b/],
  ['bedroom', /\b(bed|beds|bedroom|bedrooms|primary suite|master suite|guest room|nursery)\b/],
  ['kitchen', /\b(kitchen|kitchens|kitchenette|appliance|appliances|countertop|countertops|cabinet|cabinets|island|backsplash|range|refrigerator|fridge|pantry)\b/],
  ['dining', /\b(dining|dinette|breakfast nook)\b/],
  ['office', /\b(office|den|study|workspace|desk)\b/],
  ['outdoor', /\b(balcony|balconies|patio|terrace|deck|porch|veranda)\b/],
  ['living', /\b(living|lounge area|family room|great room|sofa|couch|fireplace)\b/],
  ['entry', /\b(entry|entryway|foyer|hallway|hall)\b/],
  ['view', /\b(view|views|window|windows|skyline)\b/],
];

export const COMMUNITY_PHOTO_CATEGORIES: CategoryMatchers = [
  ['pool', /\b(pool|pools|swimming|spa|hot tub|jacuzzi|cabana|cabanas|sundeck)\b/],
  ['fitness', /\b(fitness|gym|workout|exercise|yoga|weights|cardio|peloton|spin)\b/],
  ['cowork', /\b(cowork|coworking|co working|business center|conference|meeting room|work from home|wfh|study room)\b/],
  ['theater', /\b(theater|theatre|cinema|screening|media room)\b/],
  ['games', /\b(game|games|billiard|billiards|pool table|arcade|gaming)\b/],
  ['pet', /\b(pet|pets|dog|dogs|bark|grooming|paw)\b/],
  ['rooftop', /\b(rooftop|roof deck|roof top|sky deck|skydeck|sky lounge)\b/],
  ['grill', /\b(grill|grills|grilling|bbq|barbecue|fire pit|firepit|outdoor kitchen)\b/],
  ['courtyard', /\b(courtyard|garden|gardens|green space|park|lawn|picnic|landscap\w*)\b/],
  ['sport', /\b(court|courts|tennis|basketball|pickleball|volleyball|putting green|golf)\b/],
  ['playground', /\b(playground|play area|tot lot|kids room|children)\b/],
  ['clubroom', /\b(clubroom|club room|clubhouse|club house|lounge|resident lounge|social|community room|community kitchen|demonstration kitchen|bar)\b/],
  ['lobby', /\b(lobby|entrance|entry|concierge|reception|leasing|front desk|mailroom|mail room|package|packages)\b/],
  ['storage', /\b(bike|bicycle|storage|parking|garage|ev charging|charging)\b/],
  ['exterior', /\b(exterior|building|facade|aerial|street|outside|night)\b/],
];

function urlPath(url: string | null | undefined): string {
  if (!url) return '';
  try {
    return decodeURIComponent(new URL(url).pathname);
  } catch {
    return url;
  }
}

function photoText(photo: DiversifiablePhoto): string {
  return [photo.alt_text, photo.description, urlPath(photo.image), urlPath(photo.original_url)]
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ');
}

function categorize(photo: DiversifiablePhoto, matchers: CategoryMatchers): string | null {
  const text = photoText(photo);
  if (!text.trim()) return null;
  for (const [category, pattern] of matchers) {
    if (pattern.test(text)) return category;
  }
  return null;
}

/**
 * Reorders photos so consecutive photos show different spaces: one photo per
 * detected category per round, keeping the original order within a category
 * and between rounds. Photos that can't be categorized are treated as unique.
 * Duplicate image URLs are dropped.
 */
export function diversifyPhotos<T extends DiversifiablePhoto>(
  photos: T[],
  matchers: CategoryMatchers,
  getSrc: (photo: T) => string,
): T[] {
  const seen = new Set<string>();
  const buckets: T[][] = [];
  const bucketByCategory = new Map<string, T[]>();

  for (const photo of photos) {
    const src = getSrc(photo);
    if (seen.has(src)) continue;
    seen.add(src);

    const category = categorize(photo, matchers);
    if (category === null) {
      buckets.push([photo]);
      continue;
    }
    let bucket = bucketByCategory.get(category);
    if (!bucket) {
      bucket = [];
      bucketByCategory.set(category, bucket);
      buckets.push(bucket);
    }
    bucket.push(photo);
  }

  const result: T[] = [];
  for (let round = 0; result.length < seen.size; round += 1) {
    for (const bucket of buckets) {
      if (round < bucket.length) result.push(bucket[round]);
    }
  }
  return result;
}
